import axios, { AxiosError, type AxiosResponse } from "axios";
import Configuration from "./config";
import { store } from "../../redux/store";
import { updateAccessToken } from "../../redux/slices/user";

const V1_API = axios.create({
  baseURL: `${Configuration.baseUrl}/api/v1`,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true,
  timeout: 30000,
});

// Request interceptor: attach token
V1_API.interceptors.request.use(
  (config) => {
    const token =
      store.getState().auth.access_token || localStorage.getItem("accessToken");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(new Error(error.message || "Request failed"))
);

// Response interceptor: handle errors gracefully
V1_API.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;

    if (status === 401) {
      // Clear token and storage on authentication failure
      store.dispatch(updateAccessToken(null));
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");

      // Only redirect if we're not already on the login page
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/login")) {
        console.warn("Session expired. Redirecting to login.");
        window.location.href = "/login";
      }

      return Promise.reject(new Error("Session expired. Please login again."));
    }

    // Handle other error statuses
    if (status === 403) {
      console.warn("Access forbidden. Insufficient permissions.");
    } else if (status === 429) {
      console.warn("Too many requests. Please try again later.");
    } else if (status && status >= 500) {
      console.error("Server error. Please try again.");
    }

    return Promise.reject(
      error.response?.data
        ? new Error(
            (
              error.response.data as {
                message?: string;
                error?: string;
              }
            ).message ||
              (error.response.data as { error: string }).error ||
              "Request failed"
          )
        : new Error((error as Error).message || "Request failed")
    );
  }
);

export default V1_API;
